<template>
  <main class="l-main p-projects">
    <TitleMain icon="projects">
      <span class="title">
        {{ isAdmin || isAgency ? '管理しているサイト一覧' : 'サイト一覧' }}
      </span>
      <span v-if="hasExpirationDate" class="p-projects-expiration">
        <span>トライアルプラン 有効期限</span>
        <span class="c-text--date">{{ expirationDate }}</span>
      </span>
    </TitleMain>
    <div class="l-container">
      <div v-if="projects.length || hasOrganizations" class="p-projects-action">
        <div class="p-projects-action__buttons">
          <Button
            v-if="projects.length && !$constants.isDemo && isAuthority"
            :to="{ name: 'set' }"
            variant="main"
            fsize="f16"
            icon="plus"
            class="l-pr-4"
            :disabled="isLimited"
          >
            新規サイトを追加
          </Button>
          <Button
            v-if="hasOrganizations"
            :to="{
              name: 'organization'
            }"
            variant="light"
            icon="pageback"
            radius
          >
            お客様一覧に戻る
          </Button>
        </div>
        <div class="p-projects-action__filter">
          <SearchForm
            :value="params.filter"
            size="sm"
            @change="updateFilter"
            @reset="resetFilter"
            placeholder="検索したいサイト名を入力"
          />
          <Pager
            v-if="hasPager"
            :limit="params.limit"
            :offset="params.offset"
            :total="filteredProjects.length"
            @change="updateOffset"
          />
        </div>
      </div>
      <ListProject
        :items="projects"
        :slicedItems="slicedProjects"
        :isLimited="isLimited"
        :isLoading="isLoading"
        @delete="deleteItem"
      />
      <ModalDemo v-if="isOpenDemoModal" @close="closeDemoModal" />
      <ModalTutorial
        v-if="!isOpenDemoModal && isOpenTutorial && !isNeverOpenTutorial"
        :projectId="firstProjectId"
        @close="closeTutorialModal"
      />
    </div>
  </main>
</template>

<script>
import Button from '@/components/Button';
import ListProject from '@/components/projects/ListProject';
import ModalDemo from '@/components/projects/ModalDemo';
import ModalTutorial from '@/components/projects/ModalTutorial';
import Pager from '@/components/Pager';
import SearchForm from '@/components/SearchForm';
import TitleMain from '@/components/TitleMain';
import apiUsages from '@/api/usages';
import apiOrganizations from '@/api/organizations';

export default {
  components: {
    Button,
    ListProject,
    ModalDemo,
    ModalTutorial,
    Pager,
    SearchForm,
    TitleMain
  },
  data() {
    return {
      params: {
        filter: '',
        limit: 15,
        offset: 0
      },
      isLimited: true,
      isLoading: true,
      expirationDate: ''
    };
  },
  computed: {
    firstProjectId() {
      return this.projects.length ? this.projects[0].id : null;
    },
    filteredProjects() {
      if (!this.params.filter) {
        return this.projects;
      }
      return this.projects.filter(project => {
        return project.name
          .toLowerCase()
          .includes(this.params.filter.toLowerCase());
      });
    },
    slicedProjects() {
      return this.filteredProjects.slice(
        this.params.offset,
        this.params.offset + this.params.limit
      );
    },
    organization() {
      return this.$store.getters['view/organization'];
    },
    projects() {
      return this.$store.state.user.data.projects;
    },
    hasOrganizations() {
      return this.$store.getters['user/hasOrganizations'];
    },
    isAdmin() {
      return this.$store.state.user.status.isAdmin;
    },
    isAgency() {
      return this.$store.state.user.status.isAgency;
    },
    isAuthority() {
      return this.organization.isAuthority;
    },
    isOpenDemoModal() {
      return this.$store.state.demo.status.isOpenModal;
    },
    isNeverOpenTutorial() {
      return this.$store.state.page.tutorial.isNeverOpenModal;
    },
    isOpenTutorial() {
      return this.$store.state.page.tutorial.isOpenModal;
    },
    hasExpirationDate() {
      return this.hasOrganizations && this.expirationDate !== '';
    },
    hasPager() {
      return this.projects.length > this.params.limit;
    }
  },
  created() {
    this.getProjects();
    this.getUsages();
    this.addSubscriptions();
  },
  methods: {
    closeDemoModal() {
      this.$store.dispatch('demo/setStatus', {
        isOpenModal: false
      });
    },
    closeTutorialModal(checked) {
      this.setStatus({
        page: 'tutorial',
        status: {
          isNeverOpenModal: checked,
          isOpenModal: false
        }
      });
    },
    deleteItem() {
      this.getProjects();
      this.getUsages();
    },
    getProjects() {
      this.$store.dispatch('user/getProjects').finally(() => {
        this.isLoading = false;

        if (this.isOpenDemoModal) {
          this.setRulerStatus(this.projects);
        }
      });
    },
    getUsages() {
      return Promise.all([this.getUsagesCount(), this.getUsagesProject()])
        .then(response => {
          const count = response[0].data; //サイト登録回数
          const project = response[1].data.items[0]; //サイト数
          this.isLimited =
            count.current >= count.max || project.current >= project.max;
        })
        .catch(error => {});
    },
    getUsagesCount() {
      const params = {
        items: 'project'
      };
      return apiUsages.count.get(params, this.auth.idToken);
    },
    getUsagesProject() {
      const params = {
        items: 'projects',
        projects: 'all'
      };
      return apiUsages.get(params, this.auth.idToken);
    },
    setRulerStatus(projects) {
      // デモサイトの自社キーワード分析の基準線の初期位置を設定
      const status = {};
      for (let i = 0; i < projects.length; i++) {
        const project = projects[i];
        status[project.id] = {
          baseLine: {
            x: 500,
            y: 5,
            cv_x: 500,
            cv_y: 5
          },
          // デモサイトで表示する絞り込み条件
          filter: [
            {
              id: 'abcdefgh',
              name: '絞り込み条件1',
              date: '2020-12-01',
              value: {
                ctr: [0.1, 100],
                impressions: [0, 100000],
                influx: [0, 1000],
                keywords: ['seo', 'google'],
                keywordMatch: 'phrase',
                rank: [1, 100],
                searchVolume: [0, 1000000],
                urls: [],
                urlMatch: 'prefix',
                directConversion: [0, 100],
                assistedConversion: [0, 100],
                directConversionRate: [0, 100],
                assistedConversionRate: [0, 100]
              }
            }
          ]
        };
      }
      this.setStatus({
        page: 'siteKeywordAnalysis',
        status: status
      });
    },
    setStatus(status) {
      this.$store.dispatch('page/setStatus', status);
    },
    updateFilter(value) {
      this.params.filter = value;
      this.params.offset = 0;
    },
    resetFilter() {
      this.params.filter = '';
      this.params.offset = 0;
    },
    updateOffset(offset) {
      this.params.offset = offset;
    },
    // 契約情報を追加
    addSubscriptions() {
      return apiOrganizations.subscriptions
        .get(this.auth.idToken, this.organization.organizationId)
        .then(response => {
          if (
            response.data.subscriptions.base[0].name ===
            '無料キャンペーンプラン'
          ) {
            this.$set(
              this,
              'expirationDate',
              response.data.subscriptions.base[0].expirationDate
            );
          }
        });
    }
  }
};
</script>
